import { useState } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Typography,
  Tabs,
  message,
  Modal,
  Result,
  Checkbox,
} from "antd";
import styled from "@emotion/styled";
import { supabase } from "../lib/supabase";
import { AuthError } from "@supabase/supabase-js";
import { MailOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { keyframes } from "@emotion/react";
import { useTranslation } from "react-i18next";
import { trackAuthEvent } from "../lib/posthog";
import { Link } from "react-router-dom";
import { LanguageSwitcher } from "../components/LanguageSwitcher";

const { Title } = Typography;

const AuthContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: #ffffff;
  padding: 1rem;
`;

const Logo = styled.img`
  width: 120px;
  height: auto;
  margin-bottom: 24px;
  max-width: 100%;
  object-fit: contain;
  display: block;
  margin-left: auto;
  margin-right: auto;
`;

const StyledCard = styled(Card)`
  width: 100%;
  max-width: 400px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  @media (max-width: 480px) {
    box-shadow: none;
    background: transparent;
    .ant-card-body {
      padding: 16px 0;
    }
  }
`;

const bounceAnimation = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-30px);
  }
  60% {
    transform: translateY(-15px);
  }
`;

const EmailIcon = styled(MailOutlined)`
  font-size: 64px;
  color: #1890ff;
  margin-bottom: 24px;
  animation: ${bounceAnimation} 2s ease infinite;
`;

const ConfirmationModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 12px;
    overflow: hidden;
  }

  .ant-result-icon {
    margin-bottom: 24px;
  }

  .ant-result-title {
    font-size: 24px;
    font-weight: 600;
  }

  .ant-result-subtitle {
    font-size: 16px;
    margin-top: 16px;
    max-width: 80%;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.6;
  }
`;

export const Auth = () => {
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const { t } = useTranslation();
  const [forgotPasswordForm] = Form.useForm();

  // Function to create a new company and link it to the user
  const createCompanyForUser = async (userId: string) => {
    try {
      console.log("COMPANY CREATION: Starting for user ID:", userId);

      // 1. First, disable RLS temporarily for this operation
      const { error: disableRlsError } = await supabase.rpc(
        "admin_disable_rls"
      );
      if (disableRlsError) {
        console.error(
          "COMPANY CREATION: Failed to disable RLS:",
          disableRlsError
        );
        // Continue anyway, as we might still be able to create the company
      } else {
        console.log("COMPANY CREATION: RLS temporarily disabled");
      }

      // 2. Create the company directly
      console.log("COMPANY CREATION: Inserting company record");
      const { data: companyData, error: companyError } = await supabase
        .from("company")
        .insert([
          {
            name: "New Company",
            status: "unapproved",
            created_by_user_id: userId,
            street: "",
            house_number: "",
          },
        ])
        .select("id")
        .single();

      // 3. Re-enable RLS regardless of the outcome
      const { error: enableRlsError } = await supabase.rpc("admin_enable_rls");
      if (enableRlsError) {
        console.error(
          "COMPANY CREATION: Failed to re-enable RLS:",
          enableRlsError
        );
      } else {
        console.log("COMPANY CREATION: RLS re-enabled");
      }

      // 4. Handle company creation errors
      if (companyError) {
        console.error(
          "COMPANY CREATION: Error creating company:",
          companyError
        );

        // Try alternative approach - direct SQL insert
        console.log("COMPANY CREATION: Trying alternative approach");
        const { data: altData, error: altError } = await supabase.rpc(
          "create_company_direct",
          {
            user_id_param: userId,
            company_name_param: "New Company",
            company_status_param: "unapproved",
            street_param: "",
            house_number_param: "",
          }
        );

        if (altError) {
          console.error(
            "COMPANY CREATION: Alternative approach failed:",
            altError
          );
          return false;
        }

        if (!altData) {
          console.error(
            "COMPANY CREATION: Alternative approach returned no data"
          );
          return false;
        }

        console.log(
          "COMPANY CREATION: Company created via alternative approach, ID:",
          altData
        );

        // Link user to company
        await linkUserToCompany(userId, altData);

        // Set as selected company
        localStorage.setItem("selectedCompany", altData.toString());

        return true;
      }

      if (!companyData) {
        console.error(
          "COMPANY CREATION: No company data returned after creation"
        );
        return false;
      }

      console.log(
        "COMPANY CREATION: Company created successfully with ID:",
        companyData.id
      );

      // 5. Link user to company
      await linkUserToCompany(userId, companyData.id);

      // 6. Set as selected company
      localStorage.setItem("selectedCompany", companyData.id.toString());

      return true;
    } catch (error) {
      console.error("COMPANY CREATION: Unexpected error:", error);
      return false;
    }
  };

  // Helper function to link user to company
  const linkUserToCompany = async (userId: string, companyId: number) => {
    try {
      console.log("COMPANY CREATION: Linking user to company", {
        userId,
        companyId,
      });

      const { error: linkError } = await supabase
        .from("user_companies")
        .insert([
          {
            company_id: companyId,
            user_id: userId,
          },
        ]);

      if (linkError) {
        console.error(
          "COMPANY CREATION: Error linking user to company:",
          linkError
        );
        return false;
      }

      console.log("COMPANY CREATION: User successfully linked to company");
      return true;
    } catch (error) {
      console.error("COMPANY CREATION: Error in linking process:", error);
      return false;
    }
  };

  const handleAuth = async (
    values: {
      email: string;
      password: string;
      firstName?: string;
      lastName?: string;
      privacyConsent?: boolean;
    },
    type: "login" | "signup"
  ) => {
    try {
      // For signup, validate privacy policy consent
      if (type === "signup" && !values.privacyConsent) {
        messageApi.error(t("auth.privacyPolicyConsentRequired"));
        return;
      }

      setLoading(true);
      console.log(`Auth: Attempting to ${type} with email: ${values.email}`);

      // Check if there's any impersonation data before clearing
      const impersonatedUserId = localStorage.getItem("impersonatedUserId");
      const originalUserId = localStorage.getItem("originalUserId");
      console.log("Auth: Current impersonation state:", {
        impersonatedUserId,
        originalUserId,
      });

      // Clear any impersonation data before login attempt
      if (impersonatedUserId || originalUserId) {
        console.log("Auth: Clearing impersonation data before login");
        localStorage.removeItem("impersonatedUserId");
        localStorage.removeItem("originalUserId");
      }

      let result;

      if (type === "signup") {
        console.log("Auth: Attempting signup with Supabase");
        result = await supabase.auth.signUp({
          email: values.email,
          password: values.password,
          options: {
            data: {
              first_name: values.firstName,
              last_name: values.lastName,
            },
          },
        });

        // If signup was successful, store the user ID for company creation after confirmation
        if (result.data?.user && !result.error) {
          const userId = result.data.user.id;
          console.log("Auth: Signup successful, user ID:", userId);

          // Store user ID for company creation after email confirmation
          localStorage.setItem("pendingCompanyCreation", userId);
          console.log("Auth: Stored user ID for pending company creation");

          // If we have a session immediately (rare case where email confirmation is not required)
          if (result.data.session) {
            console.log(
              "Auth: Session available immediately, attempting company creation"
            );
            const success = await createCompanyForUser(userId);
            if (success) {
              console.log("Auth: Company created successfully during signup");
              localStorage.removeItem("pendingCompanyCreation");
            } else {
              console.error("Auth: Failed to create company during signup");
            }
          } else {
            console.log(
              "Auth: No session available, company will be created after email confirmation"
            );
          }
        }
      } else {
        // Login flow
        console.log("Auth: Attempting login with Supabase");
        result = await supabase.auth.signInWithPassword({
          email: values.email,
          password: values.password,
        });

        // If login was successful, check if we need to create a company
        if (result.data?.user && !result.error) {
          const userId = result.data.user.id;
          console.log("Auth: Login successful, user ID:", userId);

          // Check for pending company creation
          const pendingUserId = localStorage.getItem("pendingCompanyCreation");
          if (pendingUserId && pendingUserId === userId) {
            console.log("Auth: Found pending company creation for this user");

            // Create company for this user
            console.log("Auth: Attempting to create company after login");
            const success = await createCompanyForUser(userId);

            if (success) {
              console.log("Auth: Company created successfully after login");
              localStorage.removeItem("pendingCompanyCreation");
            } else {
              console.error("Auth: Failed to create company after login");
              // Keep the pendingCompanyCreation flag for next login attempt
            }
          }
        }
      }

      console.log(`Auth: ${type} result:`, {
        user: result.data?.user
          ? { id: result.data.user.id, email: result.data.user.email }
          : null,
        session: result.data?.session ? "Session present" : "No session",
        error: result.error,
      });

      if (result.error) throw result.error;

      if (type === "signup") {
        // Show confirmation modal for signup
        setUserEmail(values.email);
        setShowConfirmation(true);
      } else {
        messageApi.success("Logged in successfully!");
        console.log("Auth: Login successful, redirecting to dashboard...");
        // Redirect immediately without setTimeout
        window.location.href = "/dashboard";
      }

      // Track sign-in or sign-up event with PostHog
      trackAuthEvent(type === "signup" ? "sign_up" : "sign_in", {
        email: values.email,
        success: !result.error,
        error_message: result.error ? String(result.error) : null,
      });
    } catch (error) {
      console.error(`Auth: ${type} error:`, error);
      if (error instanceof AuthError) {
        messageApi.error(error.message);
      } else {
        messageApi.error("An unexpected error occurred");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmationClose = () => {
    setShowConfirmation(false);
  };

  const handleLoginLinkRequest = async (values: { email: string }) => {
    try {
      setLoading(true);
      console.log("Auth: Requesting password reset for email:", values.email);

      // Get the site URL with a fallback to the current origin
      const siteUrl = import.meta.env.VITE_SITE_URL || window.location.origin;

      // Create the reset URL with explicit redirect
      const resetUrl = `${siteUrl}/reset-password`;

      console.log("Auth: Environment variables:", {
        VITE_SITE_URL: import.meta.env.VITE_SITE_URL,
        windowLocationOrigin: window.location.origin,
        finalSiteUrl: siteUrl,
      });
      console.log("Auth: Using reset URL:", resetUrl);

      // Ensure the reset URL is properly formatted
      if (!resetUrl.includes("/reset-password")) {
        console.error("❌ Auth: Reset URL is malformed:", resetUrl);
        throw new Error("Invalid reset URL configuration");
      }

      // Use simple options format to avoid confusion
      const { error } = await supabase.auth.resetPasswordForEmail(
        values.email,
        {
          redirectTo: resetUrl,
        }
      );

      if (error) {
        console.log("Auth: Error requesting password reset", {
          errorMessage: error.message,
          errorCode: error.status,
          errorName: error.name,
        });
        throw error;
      }

      console.log("Auth: Password reset email sent successfully");
      setUserEmail(values.email);
      setShowForgotPassword(false);
      setShowConfirmation(true);
      forgotPasswordForm.resetFields();

      // Track password reset request event with PostHog
      trackAuthEvent("password_recovery", {
        email: values.email,
        success: true,
        error: null,
      });
    } catch (error) {
      console.error("Auth: Exception during password reset request", error);
      if (error instanceof AuthError) {
        messageApi.error(error.message);
      } else {
        messageApi.error("An unexpected error occurred");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContainer>
      {contextHolder}
      <StyledCard>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: "16px",
          }}
        >
          <LanguageSwitcher />
        </div>
        <Logo src="/logo_black.png" alt="Logo" />
        <Title level={2} style={{ textAlign: "center", marginBottom: 24 }}>
          {t("auth.welcome")}
        </Title>

        <Tabs
          centered
          items={[
            {
              key: "login",
              label: t("auth.signIn"),
              children: (
                <Form onFinish={(values) => handleAuth(values, "login")}>
                  <Form.Item
                    name="email"
                    rules={[{ required: true, type: "email" }]}
                  >
                    <Input placeholder={t("auth.email")} />
                  </Form.Item>
                  <Form.Item name="password" rules={[{ required: true }]}>
                    <Input.Password placeholder={t("auth.password")} />
                  </Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    loading={loading}
                  >
                    {t("auth.signIn")}
                  </Button>
                  <Button
                    type="link"
                    block
                    onClick={() => setShowForgotPassword(true)}
                    style={{ marginTop: 8 }}
                  >
                    {t("auth.forgotPassword")}
                  </Button>
                </Form>
              ),
            },
            {
              key: "signup",
              label: t("auth.signUp"),
              children: (
                <Form onFinish={(values) => handleAuth(values, "signup")}>
                  <Form.Item
                    name="firstName"
                    rules={[
                      { required: true, message: t("auth.firstNameRequired") },
                    ]}
                  >
                    <Input placeholder={t("auth.firstName")} />
                  </Form.Item>
                  <Form.Item
                    name="lastName"
                    rules={[
                      { required: true, message: t("auth.lastNameRequired") },
                    ]}
                  >
                    <Input placeholder={t("auth.lastName")} />
                  </Form.Item>
                  <Form.Item
                    name="email"
                    rules={[{ required: true, type: "email" }]}
                  >
                    <Input placeholder="Email" />
                  </Form.Item>
                  <Form.Item
                    name="password"
                    rules={[{ required: true, min: 6 }]}
                  >
                    <Input.Password placeholder="Password" />
                  </Form.Item>
                  <Form.Item
                    name="privacyConsent"
                    valuePropName="checked"
                    rules={[
                      {
                        validator: (_, value) =>
                          value
                            ? Promise.resolve()
                            : Promise.reject(
                                new Error(
                                  t("auth.privacyPolicyConsentRequired")
                                )
                              ),
                      },
                    ]}
                  >
                    <Checkbox>
                      {t("auth.privacyPolicyConsent")}{" "}
                      <Link
                        to="/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Privacy Policy
                      </Link>
                    </Checkbox>
                  </Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    block
                    loading={loading}
                  >
                    {t("auth.signUp")}
                  </Button>
                </Form>
              ),
            },
          ]}
        />
      </StyledCard>

      <Modal
        open={showForgotPassword}
        footer={null}
        onCancel={() => setShowForgotPassword(false)}
        width={400}
      >
        <Typography.Title level={3} style={{ marginBottom: 24 }}>
          {t("auth.resetPassword")}
        </Typography.Title>
        <Form form={forgotPasswordForm} onFinish={handleLoginLinkRequest}>
          <Form.Item
            name="email"
            rules={[
              { required: true, message: t("auth.emailRequired") },
              { type: "email", message: t("auth.validEmail") },
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder={t("auth.enterEmail")}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={loading}>
              {t("auth.sendResetLink")}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <ConfirmationModal
        open={showConfirmation}
        footer={null}
        closable={false}
        width={500}
        centered
      >
        <Result
          icon={<EmailIcon />}
          title={t("auth.emailSent")}
          subTitle={
            <>
              <p>
                {t("auth.passwordResetSent")} <strong>{userEmail}</strong>
              </p>
              <p>
                <strong>{t("auth.followStepsReset")}:</strong>
              </p>
              <ol
                style={{ textAlign: "left", maxWidth: "80%", margin: "0 auto" }}
              >
                <li>{t("auth.checkInboxReset")}</li>
                <li>{t("auth.clickResetLink")}</li>
              </ol>
              <p style={{ marginTop: "15px" }}>{t("auth.checkSpam")}</p>
            </>
          }
          extra={[
            <Button
              type="primary"
              key="continue"
              onClick={handleConfirmationClose}
              icon={<CheckCircleOutlined />}
              size="large"
            >
              {t("common.close")}
            </Button>,
          ]}
        />
      </ConfirmationModal>
    </AuthContainer>
  );
};
